import { useState, useRef, useEffect } from 'react';
import { Drawer } from 'antd';
import Operator from '../../components/Operator';
import DataTable from '../../components/DataTable';
import SelectedItems from '../../components/SelectedItems';
import OrgSelector from '../../components/OrgSelector';
import InputItem from '../../components/InputItem';
import SaveButton from '../../components/SaveButton';
import AvatarName from '../../components/AvatarName';
import EditDrawer from '../../pages/Organization/EditDrawer';

import request from '@/request';

export default function Organizations({ event, items = [] }) {
  const dataTableRef = useRef(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [projectFees, setProjectFees] = useState({});
  const [editingRecord, setEditingRecord] = useState(null);
  const [orgSelectorOpen, setOrgSelectorOpen] = useState(false);
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);

  const loadData = () => {
    if (dataTableRef.current?.refresh) {
      dataTableRef.current.refresh();
    }
  };

  const columns = [
    {
      title: '机构名称',
      dataIndex: 'organize',
      key: 'organize',
      render: (text, record) => (
        <AvatarName name={text} avatar={record.avatar} type="organization" />
      ),
    },
    {
      title: '负责人',
      dataIndex: 'contactName',
      key: 'contactName',
    },
    {
      title: '身份证号',
      dataIndex: 'idCard',
      key: 'idCard',
    },
    {
      title: '联系方式',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '所在城市',
      dataIndex: 'cityName',
      key: 'cityName',
    },
    {
      title: '操作',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Operator
          onEdit={() => {
            openEditDrawer(record);
            setEditingRecord(record);
          }}
          removeAPI="/events/del/leader"
          onRemove={loadData}
          title="机构"
          record={{ ...record, name: record.organize }}
        />
      ),
    },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const openDrawer = () => {
    setSelectedOrgs([]);
    setOrgSelectorOpen(true);
  };

  const openEditDrawer = (record) => {
    // 初始化项目费用数据
    const initialFees = {};
    record.itemsPrice.forEach((item) => {
      initialFees[item.itemId] = item.price;
    });
    setProjectFees(initialFees);
    setEditingRecord(record);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => {
      setSelectedOrgs([]);
      setProjectFees({});
      setEditingRecord(null);
    }, 200);
  };

  const handleToggleOrg = (org) => {
    setSelectedOrgs((prev) => {
      const next = prev.some((o) => o.id === org.id)
        ? prev.filter((o) => o.id !== org.id)
        : [...prev, org];
      if (next.length === 0) closeDrawer();
      return next;
    });
  };

  const handleOrgConfirm = () => {
    // 初始化项目费用数据
    const initialFees = {};
    items.forEach((item) => {
      initialFees[item.id] = item.applyPrice;
    });
    setProjectFees(initialFees);
    setOrgSelectorOpen(false);
    setDrawerOpen(true);
  };

  const handleFeeChange = (projectId, value) => {
    setProjectFees((prev) => ({
      ...prev,
      [projectId]: value || 0,
    }));
  };

  const handleComplete = async () => {
    if (editingRecord) {
      await request.post('/events/add/leader', {
        eventId: event.id,
        leaders: [
          {
            leaderId: editingRecord.leaderId,
            itemPrice: Object.keys(projectFees).map((itemId) => ({
              itemId: parseInt(itemId, 10),
              price: Number(projectFees[itemId]),
            })),
          },
        ],
      });
    }
    await request.post('/events/add/leader', {
      eventId: event.id,
      leaders: selectedOrgs.map((org) => ({
        leaderId: org.id,
        itemPrice: Object.keys(projectFees).map((itemId) => ({
          itemId: parseInt(itemId, 10),
          price: Number(projectFees[itemId]),
        })),
      })),
    });
    loadData();
    closeDrawer();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-slate-100">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 text-left">
            学校机构
          </h2>
          <p className="text-sm text-slate-500">*授权学校机构分发报名信息</p>
        </div>
        <button
          type="button"
          aria-label="添加学校机构"
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition cursor-pointer"
          onClick={openDrawer}
        >
          + 添加机构
        </button>
      </div>

      <DataTable
        ref={dataTableRef}
        columns={columns}
        api={`/events/leader/list?eventId=${event.id}`}
        options={[
          { value: 'name', label: '机构姓名' },
          { value: 'contactName', label: '负责人' },
          { value: 'phone', label: '机构手机号' },
        ]}
      />

      <OrgSelector
        open={orgSelectorOpen}
        onClose={() => setOrgSelectorOpen(false)}
        onConfirm={handleOrgConfirm}
        selectedItems={selectedOrgs}
        onToggle={handleToggleOrg}
        okText="下一步"
        api="/events/admin/leader/list"
        addPath="/organization"
        onCreate={() => {
          setOrgSelectorOpen(false);
          setEditDrawerVisible(true);
        }}
      />

      <EditDrawer
        open={editDrawerVisible}
        onClose={() => {
          setOrgSelectorOpen(true);
          setEditDrawerVisible(false);
        }}
        onSave={() => {
          setOrgSelectorOpen(true);
          setEditDrawerVisible(false);
        }}
      />

      <Drawer
        title={
          editingRecord
            ? `编辑机构 - ${editingRecord.organize}`
            : '设置项目费用'
        }
        size={600}
        onClose={closeDrawer}
        open={drawerOpen}
        extra={<SaveButton text="保存" onSave={handleComplete} />}
      >
        <div className="space-y-6">
          {!editingRecord && selectedOrgs.length > 0 && (
            <SelectedItems
              name="机构"
              items={selectedOrgs}
              onRemove={handleToggleOrg}
            />
          )}

          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              项目报名费用设置
            </h3>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      项目名称
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      组别
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-40">
                      报名费用 (元)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">
                        {item.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {item.groupName}
                      </td>
                      <td className="px-4 py-3">
                        <InputItem
                          type="money"
                          min={0}
                          value={projectFees[item.id] ?? item.applyPrice ?? 0}
                          onChange={(value) => {
                            // 为空时直接传空
                            if (
                              value === '' ||
                              value === undefined ||
                              value === null
                            ) {
                              handleFeeChange(item.id, '');
                              return;
                            }
                            let v = value;
                            if (typeof v === 'number') v = Number(v.toFixed(2));
                            // 非0时必须>=0.01，最多2位小数
                            if (v !== 0 && v < 0.01) {
                              v = 0.01;
                            }
                            handleFeeChange(item.id, v);
                          }}
                          prefix="¥"
                          placeholder="0"
                          require={false}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              * 报名费用可根据实际情况调整，设置后将应用于所选机构
            </p>
          </div>
        </div>
      </Drawer>
    </div>
  );
}
